import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { useContext } from "react";
import { ProductOrder } from "../../data/entities";
import {
    SALE_ORDER_STATUS,
    WORKSHOP,
} from "../../data/enums";
import { AuthenticationContext } from "../../provider/AuthenticationProvider";
import Note from "../ProductOrder/single/Note";
import Priority from "../ProductOrder/single/Priority";
import {
    DoubleColumnWrapper,
    SecondColumn,
    SingleProductContentWrapper,
} from "../ProductOrder/single/Styled";
import ReserveProductList from "./ReserveProductList";
import WorkshopInfo from "./WorkshopInfo";
import { MaterialRequestLink } from "../SaleOrder/single/MaterialRequestLink";
import Upload from "../SaleOrder/single/Upload";
import { Assignees } from "../SaleOrder/single/Assignees";
import styled from "styled-components";
import {
    canManageReserveAssignments,
    getWorkshopLabel,
    RESERVE_COORDINATOR_ASSIGNMENT_ROLES,
    RESERVE_STORAGE_ASSIGNMENT_ROLES,
} from "./utils";

const ResponsiveReserveColumns = styled(DoubleColumnWrapper)`
    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const CompactWorkshopField = styled.div`
    width: 100%;
    max-width: 360px;

    @media (max-width: 600px) {
        max-width: none;
    }
`;

interface ReserveOrderFormProps {
    productOrder: ProductOrder;
    mergeProductOrder: (updates: any) => void;
    isView?: boolean;
    canEditWorkshop?: boolean;
    onAssignmentsUpdated?: () => void;
}

const ReserveOrderForm = ({
    productOrder,
    mergeProductOrder,
    isView = false,
    canEditWorkshop = false,
    onAssignmentsUpdated,
}: ReserveOrderFormProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canManageAssignments = canManageReserveAssignments(
        role,
        productOrder.workshop,
    );
    const isAssignmentLocked =
        !canManageAssignments ||
        [
            SALE_ORDER_STATUS.COMPLETED,
            SALE_ORDER_STATUS.CANCELLED,
        ].includes(String(productOrder.status) as SALE_ORDER_STATUS);

    return (
        <SingleProductContentWrapper>
            {productOrder.material_requests &&
                productOrder.material_requests.length > 0 && (
                    <MaterialRequestLink
                        materialRequests={productOrder.material_requests}
                    />
                )}
            {!isView && (
                <CompactWorkshopField data-testid="reserve-workshop-field">
                    {canEditWorkshop ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="reserve-workshop-label">
                                Xưởng sản xuất
                            </InputLabel>
                            <Select
                                labelId="reserve-workshop-label"
                                label="Xưởng sản xuất"
                                value={productOrder.workshop || ""}
                                onChange={(event) =>
                                    mergeProductOrder({
                                        workshop: event.target.value,
                                        products: [],
                                    })
                                }
                            >
                                {Object.values(WORKSHOP).map((workshop) => (
                                    <MenuItem key={workshop} value={workshop}>
                                        {getWorkshopLabel(workshop)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            fullWidth
                            size="small"
                            label="Xưởng sản xuất"
                            value={getWorkshopLabel(productOrder.workshop)}
                            InputProps={{ readOnly: true }}
                        />
                    )}
                </CompactWorkshopField>
            )}
            <ReserveProductList
                products={productOrder.products}
                workshop={productOrder.workshop}
                isView={isView}
                onChange={(products) => mergeProductOrder({ products })}
            />
            <ResponsiveReserveColumns>
                <SecondColumn>
                    {isView && (
                        <WorkshopInfo workshop={productOrder.workshop} />
                    )}
                    <Upload
                        model="product_flow.SaleOrder"
                        objectId={productOrder.id}
                        objectName={
                            (productOrder as any).order_name ||
                            productOrder.name
                        }
                    />
                </SecondColumn>
                <SecondColumn>
                    {!isView && (
                        <Priority
                            productOrder={productOrder}
                            mergeProductOrder={mergeProductOrder}
                        />
                    )}
                    {isView && (
                        <Assignees
                            data={productOrder}
                            fields={[
                                {
                                    title: "Hậu cần điều phối",
                                    fieldName: "production_staff",
                                    role: "production_staff",
                                    assignmentRoles:
                                        RESERVE_COORDINATOR_ASSIGNMENT_ROLES,
                                    canEdit: canManageAssignments,
                                },
                                {
                                    title: "Thủ kho nhập kho",
                                    fieldName: "storage_keeper",
                                    role: "storage_keeper",
                                    assignmentRoles:
                                        RESERVE_STORAGE_ASSIGNMENT_ROLES,
                                    canEdit: canManageAssignments,
                                },
                            ]}
                            onAssignmentsUpdated={onAssignmentsUpdated}
                            disabledEdit={isAssignmentLocked}
                            ignoreStatusLock
                        />
                    )}
                    <Note
                        text={
                            productOrder.note ||
                            (productOrder as any).notes ||
                            ""
                        }
                        setText={(value: string) =>
                            mergeProductOrder({
                                note: value,
                                notes: value,
                            })
                        }
                        isView={isView}
                    />
                </SecondColumn>
            </ResponsiveReserveColumns>
        </SingleProductContentWrapper>
    );
};

export default ReserveOrderForm;
